/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { CustomFieldResource, CustomFieldValueResource } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import BaseComponent from "~/components/BaseComponent/index";
import type { DoBusyTask } from "~/components/DataBaseComponent/DataBaseComponent";
import { MultiSelect } from "~/components/MultiSelect/MultiSelect";
import type { SelectItem } from "~/components/VirtualListWithKeyboard/SelectItem";
import type FormFieldProps from "~/components/form/FormFieldProps";
import * as tenantCustomFields from "~/components/tenantCustomFields";
import CustomField from "../CustomField";
interface EnvironmentCustomFieldMultiSelectionProps extends FormFieldProps<string[]> {
    doBusyTask: DoBusyTask;
    items?: CustomFieldResource[] | (() => CustomFieldResource[]);
    autoFocus?: boolean;
}
interface EnvironmentCustomFieldSelectionState {
    customFields: CustomFieldResource[];
}
const CustomFieldResourceMultiSelect = MultiSelect<CustomFieldValueResource>();
export class EnvironmentCustomFieldMultiSelect extends BaseComponent<EnvironmentCustomFieldMultiSelectionProps, EnvironmentCustomFieldSelectionState> {
    constructor(props: EnvironmentCustomFieldMultiSelectionProps) {
        super(props);
        this.state = {
            customFields: this.props.items ? (typeof this.props.items === "function" ? this.props.items() : this.props.items).sort((t1, t2) => t1.SortOrder - t2.SortOrder) : null!,
        };
    }
    async componentDidMount() {
        await this.props.doBusyTask(async () => {
            if (!this.state.customFields) {
                const customFields = await tenantCustomFields.getAllEnvironmentCustomFields();
                this.setState({ customFields: customFields.sort((f1, f2) => f1.SortOrder - f2.SortOrder) });
            }
        });
    }
    chipRenderer = (customField: CustomFieldResource, r: CustomFieldValueResource | SelectItem, onRequestDelete: () => void) => {
        const customFieldValue = customField.Values!.find((t) => t.Id === r.Id);
        return (<CustomField onRequestDelete={onRequestDelete} deleteButtonAccessibleName={`Delete ${customFieldValue!.Name}`} description={customFieldValue!.Description} customFieldValueName={customFieldValue!.Name} customFieldValueColor={customFieldValue!.Color}/>);
    };
    onChange(customField: CustomFieldResource, customFieldValueIds: string[]) {
        const oldState = tenantCustomFields.groupByCustomField(this.props.value || []);
        const customFieldFromState = oldState.find((x) => x.name === customField.Name);
        const squashedList = customFieldValueIds.map((id) => customField.Values!.find((p) => p.Id === id)!.CanonicalTagName!);
        if (customFieldFromState) {
            customFieldFromState.customFields = squashedList;
        }
        else {
            oldState.push({ name: customField.Name, customFields: squashedList });
        }
        const newState = tenantCustomFields.flattenGroup(oldState);
        this.props.onChange!(newState);
    }
    render() {
        if (!this.state.customFields) {
            return null;
        }
        const options = this.state.customFields.map((fs, index) => {
            // No point showing the selector if this custom field has no values.
            if (!fs.Values || fs.Values.length === 0) {
                return null;
            }
            const selected = this.props.value ? fs.Values.filter((t) => this.props.value!.indexOf(t.CanonicalTagName!) !== -1).map((t) => t.Id) : [];
            const accessibleName = `environmentCustomFieldFilter-${fs.Name}`;
            return (<CustomFieldResourceMultiSelect accessibleName={accessibleName} onChange={(items) => this.onChange(fs, items)} key={fs.Id} value={selected} items={fs.Values ? fs.Values.sort((t1, t2) => t1.SortOrder - t2.SortOrder) : null!} renderChip={(r: CustomFieldValueResource | SelectItem, dc) => this.chipRenderer(fs, r, dc)} fieldName={`${fs.Name} environment custom fields`} description={fs.Description} autoFocus={this.props.autoFocus && index === 0}/>);
        });
        return <div>{options}</div>;
    }
    static displayName = "EnvironmentCustomFieldMultiSelect";
}
